import { useCallback, useMemo, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { useQuery } from 'react-query';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { Pagination } from 'components/layout/List/Paging';
import { SidebarLayout } from 'components/layout/SidebarLayout';
import BasePageWithTopNav from 'components/layout/TopNavigationLayout';
import PageMetadata from 'components/PageMetadata';
import DocumentSearchFilters from 'components/widgets/DocumentSearch/Filters';
import DocumentSearchSidebarFilters from 'components/widgets/DocumentSearch/Filters/SidebarFilters';
import NoResultsFound from 'components/widgets/DocumentSearch/NoResultsFound';
import DocumentSearchTable from 'components/widgets/DocumentSearch/Table';
import { useAnalytics } from 'hooks/useAnalytics';
import { useDebounce } from 'hooks/useDebounced';
import { useDocumentSearch } from 'hooks/useFilterDocuments';
import { useFilterUrlSync } from 'hooks/useFilterUrlSync';
import { usePaginate } from 'hooks/usePaginate';
import ElasticSearchFilterAPI from 'services/API/ElasticSearchFilterAPI';
import {
  useAddressCoords,
  useAddressFilter,
  useCityDocFilter,
  useDateCreatedFilter,
  useDateUpdatedFilter,
  useDocTypeFilter,
  useDocumentTableHeaderSorting,
  useKeywordFilter,
  useRegionFilter,
  useStateDocFilter,
} from 'state/filterDocuments';
import { TStartDate } from 'types';
import { trackSearch } from 'utils/analytics';
import { DocumentSearchPageViewSections } from 'utils/analytics/constants';
import { QUERY_PARAM } from 'utils/constants/QueryParameters';
import { Z_INDEX_DICT } from 'utils/constants/zIndex';
import { formatInteger } from 'utils/format';
import { formatFilterStartDate } from 'utils/transforms';

/**
 * Compact, URL-blob representation of the DocumentSearch filter selection.
 * Keys are intentionally short to keep the compressed `?f=` param small.
 */
type DocumentSearchFilterUrlState = {
  k?: string; // keyword
  a?: string; // address
  ac?: [number, number]; // address coords [lng, lat]
  s?: string; // state code
  r?: string[]; // region (mercator metropolitan) codes
  c?: number[]; // city ids
  dt?: string[]; // document type codes
  dc?: TStartDate; // date created interval
  du?: TStartDate; // date updated interval
  sh?: string; // sorted header
  sd?: boolean; // sort descending
};

// The keyword filter runs against a slow field, so it's only enabled when the
// non-keyword filters narrow the result set below this threshold.
const KEYWORD_RESULT_LIMIT = 2_500_000;

const containerStyle = {
  paddingBottom: 'padding_24',
  paddingTop: 'padding_24',
  paddingLeft: 'padding_32',
  paddingRight: 'padding_32',
  marginX: 'auto',
  overflowY: 'auto',
  height: '100%',
  width: '100%',
};

const tableCardStyle = {
  marginTop: 'padding_24',
};

const noResultsFoundStyle = {
  paddingTop: 'padding_8',
};

const DocumentSearch = () => {
  const {
    nextPage,
    previousPage,
    page: currentPage,
    setPageNumber,
    calcTotalPages,
  } = usePaginate();
  const { usePageViewTracking } = useAnalytics();
  usePageViewTracking({
    sectionName: DocumentSearchPageViewSections.FILING_SEARCH,
  });

  const { keywordFilter, setKeywordFilter } = useKeywordFilter();
  const { addressFilter, setAddressFilter } = useAddressFilter();
  const { addressCoords, setAddressCoords } = useAddressCoords();
  const { stateFilter, setStateFilter } = useStateDocFilter();
  const { regionFilter, setRegionFilter } = useRegionFilter();
  const { cityFilter, setCityFilter } = useCityDocFilter();
  const { dateCreatedFilter, setDateCreatedFilter } = useDateCreatedFilter();
  const { dateUpdatedFilter, setDateUpdatedFilter } = useDateUpdatedFilter();
  const { docTypeFilter, setDocTypeFilter } = useDocTypeFilter();

  const {
    isSortDescending,
    setIsSortDescending,
    sortedHeader,
    setSortedHeader,
    getCurrentSortStatusAsArray,
  } = useDocumentTableHeaderSorting();

  const sortSelection = useMemo(() => {
    return getCurrentSortStatusAsArray();
  }, [isSortDescending, sortedHeader]);

  // --- Filter <-> URL sync (?f=<compressed>) ---
  // Skip resetting the page while we hydrate filters from the URL so a shared
  // link's `page` (and the initial mount) is preserved.
  const isHydratingFromUrlRef = useRef(false);
  const isInitialPageEffectRef = useRef(true);

  const filterUrlState = useMemo<DocumentSearchFilterUrlState>(() => {
    const next: DocumentSearchFilterUrlState = {};
    if (keywordFilter) next.k = keywordFilter;
    if (addressFilter) next.a = addressFilter;
    if (addressCoords) next.ac = addressCoords;
    if (stateFilter) next.s = stateFilter;
    if (regionFilter.length > 0) next.r = regionFilter;
    if (cityFilter.length > 0) next.c = cityFilter;
    if (docTypeFilter.length > 0) next.dt = docTypeFilter;
    if (dateCreatedFilter) next.dc = dateCreatedFilter;
    if (dateUpdatedFilter) next.du = dateUpdatedFilter;
    if (sortedHeader) {
      next.sh = sortedHeader;
      next.sd = isSortDescending;
    }

    return next;
  }, [
    keywordFilter,
    addressFilter,
    addressCoords,
    stateFilter,
    regionFilter,
    cityFilter,
    docTypeFilter,
    dateCreatedFilter,
    dateUpdatedFilter,
    sortedHeader,
    isSortDescending,
  ]);

  const applyFilterUrlState = useCallback(
    (state: DocumentSearchFilterUrlState) => {
      isHydratingFromUrlRef.current = true;
      if (state.k !== undefined) setKeywordFilter(state.k);
      if (state.a !== undefined) setAddressFilter(state.a);
      if (state.ac !== undefined) setAddressCoords(state.ac);
      if (state.s !== undefined) setStateFilter(state.s);
      if (state.r !== undefined) setRegionFilter(state.r);
      if (state.c !== undefined) setCityFilter(state.c);
      if (state.dt !== undefined) setDocTypeFilter(state.dt);
      if (state.dc !== undefined) setDateCreatedFilter(state.dc);
      if (state.du !== undefined) setDateUpdatedFilter(state.du);
      if (state.sh !== undefined) {
        setSortedHeader(state.sh);
        setIsSortDescending(Boolean(state.sd));
      }
    },
    [
      setKeywordFilter,
      setAddressFilter,
      setAddressCoords,
      setStateFilter,
      setRegionFilter,
      setCityFilter,
      setDocTypeFilter,
      setDateCreatedFilter,
      setDateUpdatedFilter,
      setSortedHeader,
      setIsSortDescending,
    ],
  );

  useFilterUrlSync({
    paramKey: QUERY_PARAM.filters,
    state: filterUrlState,
    apply: applyFilterUrlState,
    debounceMs: 300,
  });

  const debouncedKeywordFilter = useDebounce(keywordFilter, 500);
  const debouncedAddressFilter = useDebounce(addressFilter, 500);

  useMemo(() => {
    if (!debouncedAddressFilter) {
      return;
    }
    trackSearch({ search_input: debouncedAddressFilter, filter_name: 'address' });
  }, [debouncedAddressFilter]);

  useMemo(() => {
    if (!debouncedKeywordFilter) {
      return;
    }
    trackSearch({ search_input: debouncedKeywordFilter, filter_name: 'keyword' });
  }, [debouncedKeywordFilter]);

  // Reset to page 1 when filters change. Skip the initial render and the
  // hydration-driven change so a shared link's `page` is preserved.
  useMemo(() => {
    if (isInitialPageEffectRef.current) {
      isInitialPageEffectRef.current = false;

      return;
    }
    if (isHydratingFromUrlRef.current) {
      isHydratingFromUrlRef.current = false;

      return;
    }
    setPageNumber(1);
  }, [
    debouncedKeywordFilter,
    debouncedAddressFilter,
    stateFilter,
    regionFilter,
    cityFilter,
    docTypeFilter,
    dateCreatedFilter,
    dateUpdatedFilter,
    sortSelection,
  ]);

  // All filters except the keyword. These drive both the keyword-eligibility
  // count and (together with the keyword) the main search.
  const baseFilterParams = useMemo(
    () => ({
      address: debouncedAddressFilter,
      state_code: stateFilter,
      mercator_metropolitan_ids: regionFilter,
      city_ids: cityFilter.length > 0 ? cityFilter : undefined,
      document_type_codes: docTypeFilter.length > 0 ? docTypeFilter : undefined,
      earliest_activity_start: formatFilterStartDate(dateCreatedFilter),
      latest_activity_start: formatFilterStartDate(dateUpdatedFilter),
      point: addressCoords,
      radius: addressCoords ? 200 : undefined,
    }),
    [
      debouncedAddressFilter,
      stateFilter,
      regionFilter,
      cityFilter,
      docTypeFilter,
      dateCreatedFilter,
      dateUpdatedFilter,
      addressCoords,
    ],
  );

  // Whenever a non-keyword filter changes, check how many filings the other
  // filters return. The keyword field stays disabled while that count is above
  // the limit so we never run the slow keyword field against a huge result set.
  const { data: keywordEligibilityCount } = useQuery(
    ['documentKeywordEligibilityCount', baseFilterParams],
    async () => {
      const response = await ElasticSearchFilterAPI.postFilterDocuments({
        ...baseFilterParams,
        page: 1,
      });

      return response?.data?.count ?? 0;
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  const isKeywordDisabled = (keywordEligibilityCount ?? 0) > KEYWORD_RESULT_LIMIT;

  const { filteredDocuments } = useDocumentSearch({
    ...baseFilterParams,
    keyword_inclusions: isKeywordDisabled ? undefined : debouncedKeywordFilter,
    page: currentPage,
    sort: sortSelection,
  });

  const totalCount = useMemo(() => {
    return filteredDocuments?.data?.count || 0;
  }, [filteredDocuments?.data]);

  const totalCountLabel = useMemo(() => {
    if (totalCount === 1) {
      return `${formatInteger(totalCount)} filing found`;
    }

    return `${formatInteger(totalCount)} filings found`;
  }, [totalCount]);

  const totalPages = useMemo(() => {
    return calcTotalPages(totalCount) || 1;
  }, [totalCount]);

  return (
    <BasePageWithTopNav>
      <PageMetadata title="Filing Search" />
      <SidebarLayout
        hideSidebarOnMobile
        sidebarZIndex={Z_INDEX_DICT.DOCUMENT_FILTERS_MENU}
        sidebar={<DocumentSearchSidebarFilters />}
      >
        <FlexColumn sx={containerStyle}>
          <DocumentSearchFilters isKeywordDisabled={isKeywordDisabled} />

          <DocumentSearchTable
            documentList={filteredDocuments?.data?.results || []}
            isLoading={filteredDocuments.isLoading}
            cardSx={tableCardStyle}
            header={totalCountLabel}
            footer={
              <Pagination
                currentPage={currentPage}
                isLoading={filteredDocuments.isLoading}
                totalPages={totalPages}
                setPage={setPageNumber}
                onNext={nextPage}
                onPrev={previousPage}
              />
            }
          />
          {totalCount === 0 && !filteredDocuments.isLoading && (
            <Box sx={noResultsFoundStyle}>
              <NoResultsFound />
            </Box>
          )}
        </FlexColumn>
      </SidebarLayout>
    </BasePageWithTopNav>
  );
};

export default DocumentSearch;
